/**
 * This file utilizes and enhances @sanity/block-content-to-react so that we can
 * more easily customize how the sanity block renderer maps its data structure to
 * react components.
 */
import React, { useMemo } from 'react';

import {
  Box,
  BoxProps,
  HStack,
  Header,
  TextProps,
  View,
  makeUclComponent,
} from '@rbilabs/universal-components';
import BlockContent from '@sanity/block-content-to-react';
import type { TextStyle } from 'react-native';
import { Text as RNText, View as RNView, StyleSheet } from 'react-native';
import {
  Typography,
  TypographyProps,
  fontBodyStyles,
  fontHeadingStyles,
} from 'ucl/components/text/Typography';

import { IBaseProps } from '@rbi-ctg/frontend';
import { ISanityBlockContent, ISanityTypographyBlock } from '@rbi-ctg/menu';
import { fontSizes } from 'styles/constants/fonts';

import { FormatSanityLink } from './helpers';
import {
  ComponentOverride,
  IBlockContentOverrides,
  ISerializers,
  ITypographyBlockOverrides,
} from './types';

const NATIVEBASE_TEXT_BASE_VARIANT = 'copyOne' as const;

type BlockFontFamilyToken = 'heading' | 'body';

const BlockFontContext = React.createContext<BlockFontFamilyToken>('body');

const HEADING_BLOCK_STYLE = /^h[1-6]$/;

const resolveBlockFontToken = (style: string | undefined): BlockFontFamilyToken =>
  style && HEADING_BLOCK_STYLE.test(style) ? 'heading' : 'body';

const markStyles = StyleSheet.create({
  strongBold: { fontWeight: 'bold' },
  emUnderline: { textDecorationLine: 'underline' },
});

type FontSizeToken = keyof typeof fontSizes;

const buildInlineMarkStyle = (
  props: { [key: string]: unknown } | undefined,
  baseStyle: TextStyle
): TextStyle[] => {
  const styles: TextStyle[] = [baseStyle];
  if (!props) {
    return styles;
  }

  const { fontFamily, fontSize, color, fontWeight, textAlign, lineHeight } = props as {
    fontFamily?: 'heading' | 'body' | string;
    fontSize?: number | string;
    color?: string;
    fontWeight?: TextStyle['fontWeight'];
    textAlign?: TextStyle['textAlign'];
    lineHeight?: number | string;
  };

  if (fontWeight !== undefined) {
    styles.push({ fontWeight });
  }
  if (typeof fontSize === 'number') {
    styles.push({ fontSize });
  } else if (typeof fontSize === 'string' && fontSize in fontSizes) {
    styles.push({ fontSize: fontSizes[fontSize as FontSizeToken] });
  }
  if (color) {
    styles.push({ color });
  }
  if (textAlign) {
    styles.push({ textAlign });
  }
  if (fontFamily === 'body') {
    styles.push({ fontFamily: fontBodyStyles.bold.fontFamily });
  } else if (fontFamily === 'heading') {
    styles.push({ fontFamily: fontHeadingStyles.bold.fontFamily });
  } else if (typeof fontFamily === 'string') {
    styles.push({ fontFamily });
  }
  if (typeof lineHeight === 'number') {
    styles.push({ lineHeight });
  }

  return styles;
};

interface IDefaultBlockRenderer extends IBaseProps {
  node: {
    style?: string;
  };
}

interface IBlockRenderer extends IDefaultBlockRenderer {
  blockContentOverrides?: IBlockContentOverrides | ITypographyBlockOverrides;
  textProps?: TextProps;
}

const blockContent: IBlockContentOverrides = {
  h1: ({ children }) => <Header variant="headerOne">{children}</Header>,
  h2: ({ children }) => <Header variant="headerTwo">{children}</Header>,
  h3: ({ children }) => <Header variant="headerThree">{children}</Header>,
  h4: ({ children }) => <Header variant="headerFour">{children}</Header>,
  h5: ({ children }) => <Header variant="headerFour">{children}</Header>,
  h6: ({ children }) => <Header variant="headerFour">{children}</Header>,
  p: ({ children, textProps }: any) => (
    <Typography variant={NATIVEBASE_TEXT_BASE_VARIANT} {...textProps}>
      {children}
    </Typography>
  ),
  quote: ({ children, textProps }: any) => (
    <Typography variant={NATIVEBASE_TEXT_BASE_VARIANT} {...textProps}>
      {children}
    </Typography>
  ),
  blockquote: ({ children, textProps }: any) => (
    <Typography variant={NATIVEBASE_TEXT_BASE_VARIANT} {...textProps}>
      {children}
    </Typography>
  ),
  normal: ({ children, textProps }: any) => (
    <Typography variant={NATIVEBASE_TEXT_BASE_VARIANT} {...textProps}>
      {children}
    </Typography>
  ),
};

// This code is based off of https://github.com/sanity-io/block-content-to-react#customizing-the-default-serializer-for-block-type
const BlockRenderer: React.FC<React.PropsWithChildren<IBlockRenderer>> = props => {
  const { node, children, blockContentOverrides = {}, textProps } = props;
  const { style = 'normal' } = node;

  // Match style to the block child tag override if it exists
  const MatchingTagOverride: ComponentOverride | undefined = (blockContentOverrides as Record<
    string,
    ComponentOverride | undefined
  >)[style];

  if (MatchingTagOverride) {
    // Keep Sanity blocks stacked on web and expose the block font family to marks.
    return (
      <BlockFontContext.Provider value={resolveBlockFontToken(style)}>
        <View>
          <MatchingTagOverride textProps={textProps}>{children}</MatchingTagOverride>
        </View>
      </BlockFontContext.Provider>
    );
  }

  // Fall back to default handling
  return BlockContent.defaultSerializers.types.block(props);
};

interface ISanityBlockRenderer extends BoxProps {
  content?: ISanityBlockContent[] | ISanityTypographyBlock[] | null;
  serializers?: ISerializers;
  blockContentOverrides?: IBlockContentOverrides | ITypographyBlockOverrides;
  textProps?: TextProps;
  linkProps?: TextProps;
  strongProps?: TextProps;
}

function toTypographyProps(textProps?: TextProps): Partial<TypographyProps> | undefined {
  if (!textProps) {
    return undefined;
  }
  const { color, ...rest } = textProps;
  return { ...rest, color: color ?? undefined } as Partial<TypographyProps>;
}

const StrongMark: React.FC<React.PropsWithChildren<{ strongProps?: TextProps }>> = ({
  strongProps,
  children,
}) => {
  const parentFontToken = React.useContext(BlockFontContext);
  const boldFontFamilyStyle: TextStyle = {
    fontFamily:
      parentFontToken === 'heading'
        ? fontHeadingStyles?.bold?.fontFamily ?? 'Flame-Bold'
        : fontBodyStyles?.bold?.fontFamily ?? 'Flame-Regular',
  };
  return (
    <RNText
      style={[
        ...buildInlineMarkStyle(
          strongProps as { [key: string]: unknown } | undefined,
          markStyles.strongBold
        ),
        boldFontFamilyStyle,
      ]}
    >
      {children}
    </RNText>
  );
};

/**
 * Creates the `list` and `listItem` serializers for @sanity/block-content-to-react.
 *
 * Nested-list detection uses `isSanityListElement` (prop-based) so it stays
 * correct even when a caller overrides `serializers.list` with a custom
 * component — the Sanity library always passes `type: 'bullet'|'number'` to
 * list elements regardless of which serializer renders them.
 */
// @sanity/block-content-to-react passes a `type` prop ('bullet' | 'number')
// exclusively to `list` serializers. Inline mark serializers (strong, em,
// link, etc.) never receive this prop, so it serves as a reliable,
// serializer-agnostic way to identify nested list elements — even when a
// caller supplies a custom `list` serializer via the `serializers` prop.
const isSanityListElement = (child: React.ReactNode): boolean =>
  React.isValidElement(child) &&
  ((child.props as Record<string, unknown>)?.type === 'bullet' ||
    (child.props as Record<string, unknown>)?.type === 'number');

const createListSerializers = (typographyProps?: Partial<TypographyProps>) => {
  const variant = typographyProps?.variant ?? 'copyOne';
  const listSerializer = ({ type, children }: { type?: string; children?: React.ReactNode }) => {
    const isBulletList = type === 'bullet';
    const items = React.Children.toArray(children);

    const listItems = items.filter(child => !isSanityListElement(child));
    const nestedLists = items.filter(isSanityListElement);

    return (
      <RNView accessibilityRole="list">
        {listItems.map((listItem, index) => {
          const indicator = isBulletList ? '\u2022' : `${index + 1}.`;
          return (
            <HStack key={index} marginTop="$1">
              <Typography {...typographyProps} variant={variant} mr="$2" aria-hidden>
                {indicator}
              </Typography>
              <RNView style={styles.listItemContent}>{listItem}</RNView>
            </HStack>
          );
        })}
        {nestedLists.map((nestedList, index) => (
          // Indent nested lists to align with parent item content
          // by reusing the same HStack + spacer layout.
          <HStack key={`nested-${index}`}>
            <Typography {...typographyProps} variant={variant} mr="$2" style={styles.hidden}>
              {'\u2022'}
            </Typography>
            <RNView style={styles.listItemContent}>{nestedList}</RNView>
          </HStack>
        ))}
      </RNView>
    );
  };

  const wrapInline = (nodes: React.ReactNode[], key: number) => (
    <Typography key={`inline-${key}`} {...typographyProps} variant={variant}>
      {nodes}
    </Typography>
  );

  const listItemSerializer = ({ children }: { children?: React.ReactNode }) => {
    const childArray = React.Children.toArray(children);

    // Fast path: when there are no nested lists every child is an inline
    // text/mark span, so a single Typography preserves inline flow.
    if (!childArray.some(isSanityListElement)) {
      return (
        <Typography {...typographyProps} variant={variant}>
          {children}
        </Typography>
      );
    }

    // Mixed content: the item has both inline spans and a nested list.
    // We must keep inline spans together inside a single Typography (so
    // marked and unmarked text stays on one line) while rendering nested
    // lists as block-level siblings — placing a list inside Typography
    // would crash React Native (View inside Text).
    const { groups, inline } = childArray.reduce<{
      groups: React.ReactNode[];
      inline: React.ReactNode[];
    }>(
      (acc, child) => {
        if (isSanityListElement(child)) {
          const flushed =
            acc.inline.length > 0
              ? [...acc.groups, wrapInline(acc.inline, acc.groups.length)]
              : acc.groups;
          return { groups: [...flushed, child], inline: [] };
        }
        return { groups: acc.groups, inline: [...acc.inline, child] };
      },
      { groups: [], inline: [] }
    );

    const finalGroups = inline.length > 0 ? [...groups, wrapInline(inline, groups.length)] : groups;

    return <RNView>{finalGroups}</RNView>;
  };

  return { list: listSerializer, listItem: listItemSerializer };
};

// See https://github.com/sanity-io/block-content-to-react
// for more options
const createDefaultSerializers = (
  blockContentOverrides: IBlockContentOverrides | ITypographyBlockOverrides,
  textProps?: TextProps,
  linkProps?: TextProps,
  strongProps?: TextProps
): ISerializers => ({
  marks: {
    small: ({ children }: any) => (
      <View>
        <Typography variant="copyTwo">{children}</Typography>
      </View>
    ),
    link: props => FormatSanityLink({ ...props, linkProps: linkProps as any }),
    strong: ({ children }: any) => <StrongMark strongProps={strongProps}>{children}</StrongMark>,
    em: ({ children }: any) => <RNText style={markStyles.emUnderline}>{children}</RNText>,
  },
  types: {
    // @ts-expect-error TS(2322) FIXME: Type '(props: IDefaultBlockRenderer) => JSX.Elemen... Remove this comment to see the full error message
    block: (props: IDefaultBlockRenderer) => (
      <BlockRenderer
        {...props}
        blockContentOverrides={blockContentOverrides}
        textProps={textProps}
      />
    ),
  },
  ...createListSerializers(toTypographyProps(textProps)),
});

const SanityBlockRenderer: React.FC<React.PropsWithChildren<ISanityBlockRenderer>> = ({
  content,
  serializers = {},
  blockContentOverrides = blockContent,
  textProps,
  linkProps,
  strongProps,
  ...props
}) => {
  const allSerializers = useMemo(() => {
    const defaultSerializers = createDefaultSerializers(
      blockContentOverrides,
      textProps,
      linkProps,
      strongProps
    );
    return { ...defaultSerializers, ...serializers };
  }, [blockContentOverrides, textProps, linkProps, strongProps, serializers]);

  if (!content) {
    return null;
  }

  return (
    <Box {...props}>
      <BlockContent
        imageOptions={empty}
        blocks={content}
        serializers={allSerializers}
        renderContainerOnSingleChild
      />
    </Box>
  );
};

// to replace defaultProps on deprecated BlockContent lib
const empty = {};

const styles = StyleSheet.create({
  listItemContent: { flex: 1 },
  hidden: { opacity: 0 },
});

const UCLSanityBlockRenderer = makeUclComponent(SanityBlockRenderer);

export default UCLSanityBlockRenderer;

/** Creates block-level overrides that render all Sanity block tags consistently. */
export const createFixedTextOverrides = ({
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
}: {
  fontFamily?: TextProps['fontFamily'];
  fontWeight?: TextProps['fontWeight'];
  fontSize?: TextProps['fontSize'] | number;
  lineHeight?: TextProps['lineHeight'] | number;
}): IBlockContentOverrides => {
  const fontWeightStyle: TextStyle | undefined =
    fontWeight !== undefined ? { fontWeight: fontWeight as TextStyle['fontWeight'] } : undefined;

  const fixedFontToken: BlockFontFamilyToken = fontFamily === 'heading' ? 'heading' : 'body';

  const FixedText = ({ children, textProps: blockTextProps }: any) => (
    <BlockFontContext.Provider value={fixedFontToken}>
      <Typography
        variant={NATIVEBASE_TEXT_BASE_VARIANT}
        {...blockTextProps}
        {...(fontFamily && { fontFamily: fontFamily as 'heading' | 'body' })}
        {...(typeof fontSize !== 'undefined' && {
          fontSize: fontSize as TypographyProps['fontSize'],
        })}
        {...(typeof lineHeight !== 'undefined' && {
          lineHeight: lineHeight as TypographyProps['lineHeight'],
        })}
        style={fontWeightStyle}
      >
        {children}
      </Typography>
    </BlockFontContext.Provider>
  );

  return {
    h1: FixedText,
    h2: FixedText,
    h3: FixedText,
    h4: FixedText,
    h5: FixedText,
    h6: FixedText,
    p: FixedText,
    quote: FixedText,
    blockquote: FixedText,
    normal: FixedText,
  };
};

/**
 * Creates a reusable typography configuration for Sanity content that unifies
 * block rendering and inline mark (link/strong) styling for consistent output.
 */
export const createFixedTypographyConfig = ({
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  linkUnderline = true,
}: {
  fontFamily?: TextProps['fontFamily'];
  fontWeight?: TextProps['fontWeight'];
  fontSize?: TextProps['fontSize'] | number;
  lineHeight?: TextProps['lineHeight'] | number;
  linkUnderline?: boolean;
}): {
  blockContentOverrides: IBlockContentOverrides | ITypographyBlockOverrides;
  textProps: TextProps;
  linkProps: TextProps;
  strongProps?: TextProps;
} => {
  const baseProps: TextProps = {
    ...(fontFamily && { fontFamily }),
    ...(fontWeight && { fontWeight }),
    ...(typeof fontSize !== 'undefined' && { fontSize }),
    ...(typeof lineHeight !== 'undefined' && { lineHeight }),
  } as TextProps;

  return {
    blockContentOverrides: createFixedTextOverrides({
      fontFamily,
      fontWeight,
      fontSize,
      lineHeight,
    }),
    textProps: baseProps,
    linkProps: { ...baseProps, underline: linkUnderline },
    strongProps: baseProps,
  };
};
